import { fetchApiJson } from './apiRequest.js';
const DEFAULT_ERROR = 'The team schedule is temporarily unavailable.';
const LIVE_SEASON = 2026;
const LIVE_CACHE_MS = 2 * 60 * 1000;
const ARCHIVE_CACHE_MS = 60 * 60 * 1000;
const responseCache = new Map();
const pendingRequests = new Map();

export async function getTeamSchedule({ season, team, signal, onProgress, force = false }) {
  const normalizedSeason = Number(season);
  const normalizedTeam = String(team || '').trim();

  if (!Number.isInteger(normalizedSeason) || !normalizedTeam) {
    throw new Error('A team and season are required.');
  }

  const cacheKey = `${normalizedSeason}:${normalizedTeam.toLowerCase()}`;
  const cached = responseCache.get(cacheKey);
  if (!force && cached && cached.expiresAt > Date.now()) {
    onProgress?.(cached.data);
    return cached.data;
  }

  let request = pendingRequests.get(cacheKey);
  if (!request) {
    request = loadTeamSchedule({ normalizedSeason, normalizedTeam, onProgress })
      .then((data) => {
        responseCache.set(cacheKey, {
          data,
          expiresAt: Date.now() + (
            data?.partial
              ? 60 * 1000
              : normalizedSeason === LIVE_SEASON ? LIVE_CACHE_MS : ARCHIVE_CACHE_MS
          ),
        });
        return data;
      })
      .finally(() => pendingRequests.delete(cacheKey));
    pendingRequests.set(cacheKey, request);
  }

  return waitForRequest(request, signal);
}

async function loadTeamSchedule({ normalizedSeason, normalizedTeam, onProgress }) {
  const payloads = [];
  const firstPayload = await fetchSchedulePage({
    normalizedSeason,
    normalizedTeam,
    page: 1,
  });
  payloads.push(firstPayload);

  const totalPages = Math.max(1, integerOrNull(firstPayload?.pagination?.totalPages) || 1);
  let paginationComplete = firstPayload?.pageCoverage?.complete !== false;

  onProgress?.(buildScheduleResponse({
    payloads,
    normalizedSeason,
    normalizedTeam,
    totalPages,
    paginationComplete: paginationComplete && totalPages === 1,
  }));

  const PAGE_CONCURRENCY = 3;
  for (let cursor = 2; cursor <= totalPages; cursor += PAGE_CONCURRENCY) {
    const pageNumbers = Array.from(
      { length: Math.min(PAGE_CONCURRENCY, totalPages - cursor + 1) },
      (_, index) => cursor + index
    );
    const results = await Promise.allSettled(pageNumbers.map((page) =>
      fetchSchedulePage({ normalizedSeason, normalizedTeam, page })
    ));
    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        payloads.push(result.value);
        if (result.value?.pageCoverage?.complete === false) paginationComplete = false;
      } else {
        paginationComplete = false;
      }
    });

    onProgress?.(buildScheduleResponse({
      payloads,
      normalizedSeason,
      normalizedTeam,
      totalPages,
      paginationComplete: paginationComplete && payloads.length >= totalPages,
    }));
  }

  return buildScheduleResponse({
    payloads,
    normalizedSeason,
    normalizedTeam,
    totalPages,
    paginationComplete,
  });
}

function buildScheduleResponse({
  payloads,
  normalizedSeason,
  normalizedTeam,
  totalPages,
  paginationComplete,
}) {
  const games = mergeScheduleGames(payloads);
  const completed = games.filter((game) => game.final).sort((a, b) => compareGames(b, a));
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = games
    .filter((game) => ['scheduled', 'live'].includes(game.status) && game.date >= today)
    .sort(compareGames);
  const expectedRecord = payloads
    .map((payload) => normalizeRecordCheck(payload?.recordCheck)?.expected)
    .find(Boolean) || null;
  const scheduleRecord = summarizeScheduleRecord(games);
  const recordComplete = paginationComplete && recordsMatch(expectedRecord, scheduleRecord);
  const first = payloads[0] || {};

  return {
    season: Number(first?.season) || normalizedSeason,
    team: first?.team || { slug: normalizedTeam, name: normalizedTeam },
    fetchedAt: payloads.map((payload) => String(payload?.fetchedAt || '')).sort().at(-1) || '',
    partial: !paginationComplete || !games.length || !recordComplete,
    counts: {
      total: games.length,
      completed: completed.length,
      upcoming: upcoming.length,
    },
    recordCheck: {
      expected: expectedRecord,
      schedule: scheduleRecord,
      complete: recordComplete,
      scoreboardSupplemented: payloads.some((payload) =>
        Boolean(payload?.recordCheck?.scoreboardSupplemented)
      ),
    },
    pagination: {
      pagesLoaded: payloads.length,
      totalPages,
      complete: paginationComplete,
    },
    games,
    recent: completed.slice(0, 5),
    upcoming: upcoming.slice(0, 5),
  };
}

async function fetchSchedulePage({ normalizedSeason, normalizedTeam, page }) {
  const params = new URLSearchParams({
    season: String(normalizedSeason),
    team: normalizedTeam,
    page: String(page),
  });
  const { response, payload } = await fetchApiJson(`/api/team-schedule?${params.toString()}`, {
    headers: { Accept: 'application/json' },
    timeoutMs: 25000,
  });

  if (!response.ok) throw new Error(payload?.message || DEFAULT_ERROR);
  return payload || {};
}

function mergeScheduleGames(payloads) {
  const games = [];
  const indexes = new Map();

  payloads.forEach((payload) => {
    (Array.isArray(payload?.games) ? payload.games : []).forEach((rawGame) => {
      const game = normalizeGame(rawGame);
      if (!game) return;
      const keys = scheduleGameKeys(game);
      const existingIndex = keys
        .map((key) => indexes.get(key))
        .find((index) => Number.isInteger(index));

      if (Number.isInteger(existingIndex)) {
        if (scheduleGameQuality(game) > scheduleGameQuality(games[existingIndex])) {
          games[existingIndex] = game;
        }
        scheduleGameKeys(games[existingIndex]).forEach((key) => indexes.set(key, existingIndex));
        return;
      }

      const index = games.length;
      games.push(game);
      keys.forEach((key) => indexes.set(key, index));
    });
  });

  return games.sort(compareGames);
}

function scheduleGameKeys(game) {
  const keys = [];
  if (game?.gameId) keys.push(`id:${game.gameId}`);
  const teams = [game?.away, game?.home]
    .map((team) => String(team?.slug || team?.name || '').toLowerCase().replace(/[^a-z0-9]/g, ''))
    .filter(Boolean)
    .sort();
  if (game?.date && teams.length === 2) keys.push(`match:${game.date}|${teams.join('|')}`);
  return [...new Set(keys)];
}

function scheduleGameQuality(game) {
  return (game?.final ? 20 : game?.live ? 10 : 0) +
    (['win', 'loss'].includes(game?.teamPerspective?.result) ? 8 : 0) +
    (Number.isInteger(game?.teamPerspective?.score) ? 4 : 0) +
    (Array.isArray(game?.away?.setScores) ? game.away.setScores.length : 0) +
    (Array.isArray(game?.home?.setScores) ? game.home.setScores.length : 0) +
    (game?.gameId ? 2 : 0);
}

function summarizeScheduleRecord(games) {
  let wins = 0;
  let losses = 0;
  let unknown = 0;

  games.forEach((game) => {
    if (!game?.final) return;
    if (game.teamPerspective?.result === 'win') wins += 1;
    else if (game.teamPerspective?.result === 'loss') losses += 1;
    else unknown += 1;
  });

  return {
    wins,
    losses,
    matches: wins + losses,
    unknown,
    label: `${wins}-${losses}`,
  };
}

function recordsMatch(expected, schedule) {
  if (!expected) return Boolean(schedule);
  return expected.wins === schedule?.wins &&
    expected.losses === schedule?.losses &&
    expected.matches === schedule?.matches &&
    !schedule?.unknown;
}

function normalizeRecordCheck(value) {
  if (!value || typeof value !== 'object') return null;
  return {
    expected: normalizeRecord(value.expected),
    schedule: normalizeRecord(value.schedule),
    complete: Boolean(value.complete),
    scoreboardSupplemented: Boolean(value.scoreboardSupplemented),
  };
}

function normalizeRecord(value) {
  if (!value || typeof value !== 'object') return null;
  const wins = integerOrNull(value.wins);
  const losses = integerOrNull(value.losses);
  const matches = integerOrNull(value.matches);
  const unknown = integerOrNull(value.unknown);
  return {
    wins,
    losses,
    matches,
    unknown,
    label: String(
      value.label ||
      (wins !== null && losses !== null ? `${wins}-${losses}` : '')
    ),
  };
}

function normalizeGame(game) {
  const date = String(game?.date || '').trim();
  if (!date) return null;
  const rawId = String(game?.gameId || game?.id || '').trim();
  const gameId = /^[1-9]\d*$/.test(rawId) ? rawId : '';
  const away = normalizeTeam(game?.away);
  const home = normalizeTeam(game?.home);
  const perspective = game?.teamPerspective || {};
  const started = game?.final || game?.live || ['live', 'final'].includes(game?.status);
  if (!started) { away.score = null; home.score = null; away.setScores = []; home.setScores = []; }

  return {
    ...game,
    id: gameId || String(game?.id || `${date}-${away.slug}-${home.slug}`),
    gameId,
    date,
    startTime: String(game?.startTime || ''),
    status: ['final', 'live', 'scheduled'].includes(game?.status) ? game.status : game?.final ? 'final' : game?.live ? 'live' : 'scheduled',
    statusLabel: String(game?.statusLabel || (game?.final ? 'Final' : game?.live ? 'Live' : 'Scheduled')),
    timeLabel: String(game?.timeLabel || ''),
    final: Boolean(game?.final || game?.status === 'final'),
    live: Boolean(game?.live || game?.status === 'live'),
    scheduled: Boolean(game?.scheduled || (!game?.final && !game?.live)),
    matchCenterAvailable: Boolean(gameId && game?.matchCenterAvailable !== false),
    venue: String(game?.venue || ''),
    network: String(game?.network || ''),
    away,
    home,
    teamPerspective: {
      side: perspective.side === 'home' ? 'home' : 'away',
      result: perspective.result === 'win' ? 'win' : perspective.result === 'loss' ? 'loss' : '',
      score: started ? integerOrNull(perspective.score) : null,
      opponentScore: started ? integerOrNull(perspective.opponentScore) : null,
      opponentName: String(perspective.opponentName || '').trim(),
      opponentSlug: String(perspective.opponentSlug || '').trim(),
      site: game.neutralSite || perspective.site === 'neutral' ? 'neutral' : perspective.site === 'home' ? 'home' : 'away',
    },
  };
}

function normalizeTeam(team) {
  return {
    ...(team || {}),
    id: String(team?.id || ''),
    name: String(team?.name || '').trim(),
    slug: String(team?.slug || '').trim(),
    score: integerOrNull(team?.score),
    winner: Boolean(team?.winner),
    rank: integerOrNull(team?.rank),
    record: String(team?.record || ''),
    conference: String(team?.conference || ''),
    setScores: Array.isArray(team?.setScores)
      ? team.setScores.map(integerOrNull)
      : [],
  };
}

function compareGames(left, right) {
  return left.date.localeCompare(right.date) ||
    String(left.startTime || '').localeCompare(String(right.startTime || '')) ||
    String(left.gameId || '').localeCompare(String(right.gameId || ''));
}

function integerOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isInteger(number) ? number : null;
}

async function readJson(response) {
  try { return await response.json(); } catch { return null; }
}

function waitForRequest(request, signal) {
  if (!signal) return request;
  if (signal.aborted) return Promise.reject(abortError());

  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    request.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      }
    );
  });
}

function abortError() {
  const error = new Error('Aborted');
  error.name = 'AbortError';
  return error;
}
